import { z } from 'zod';
import type { AiMode } from '../constants';
import type { StateResponse } from './setup/states';
import type { KanbanOutcomeInput } from './setup/kanban';
import type { CallParticipantType } from './calls';
import type { HubCallOpening } from './call-openings';
import type { HubPresentationMaps } from './hub-presentation';

// A task title's voice-note value, for clients rendering `ConversationItemResponse.title`
// (wayai#4640). `deriveTaskTitle`, which writes titles, is exported from the package root.
export { TASK_TITLE_VOICE, isVoiceTaskTitle } from '../task-title';

// ---------------------------------------------------------------------------
// Chat Conversations
// ---------------------------------------------------------------------------

// 'chat_task' is an internal combined-fetch value — /chat and /task share a
// single cache keyed on this; the response carries both hub_type values and
// the frontend filters client-side. Keeping it in the enum keeps typed
// clients (Hono RPC, MCP, CLI) aware of the shape.
/**
 * ACTIVE-list row window, shared by the list route and the client's per-hub loader.
 *
 * One declaration because the two sides are coupled by arithmetic, not by convention: the
 * client steps `page_size` up by the default and stops at the max, and the route clamps to
 * the same pair. Declared apart, raising the server clamp would leave rows behind a button
 * the client already retired, and lowering it would leave a button re-fetching identical
 * rows forever.
 */
export const ACTIVE_LIST_DEFAULT_LIMIT = 50;
export const ACTIVE_LIST_MAX_LIMIT = 200;

export const getConversationsQuery = z.object({
  nav_item: z.enum(['chat', 'task', 'support', 'chat_task']),
  hub_team_user_id: z.string().optional(),
  hub_user_id: z.string().optional(),
  search_filter: z.string().optional(),
  hub_id: z.string().uuid().optional(),
  // NON-authorizing single-hub narrow. Unlike `hub_id` (which runs the
  // grant-gated `loadHub('hub:read')` and so throws for grant-less end users /
  // team members), `scope_hub_id` INTERSECTS the caller's already-resolved hub
  // roster server-side. Intersecting the caller's own roster IS the
  // authorization — a hub the caller isn't in narrows to empty and leaks
  // nothing. Continuous history sends it to take the single-hub ended-index
  // path (one HubDO read, accurate uncapped total) instead of the all-hub
  // fan-out it would otherwise discard client-side (gh #3319).
  scope_hub_id: z.string().uuid().optional(),
  conversation_id: z.string().uuid().optional(),
  // 'ended' = recently-closed, served warm from the HubDO index (≤ retention
  // window). 'archived' = the full closed-conversation history from ClickHouse.
  status: z.enum(['active', 'agent', 'team', 'ended', 'archived']).optional(),
  page: z.string().regex(/^\d+$/).optional(),
  page_size: z.string().regex(/^\d+$/).optional(),
});

// ---------------------------------------------------------------------------
// Chat Messages
// ---------------------------------------------------------------------------

export const getMessagesQuery = z.object({
  hub_id: z.string().uuid(),
  hub_team_user_id: z.string().optional(),
  hub_user_id: z.string().optional(),
  conversation_id: z.string().uuid().optional(),
  message_id: z.string().uuid().optional(),
  // `page` = LEGACY oldest-first offset paging (deployed page-based clients).
  // Its PRESENCE selects the legacy read path server-side; a new client omits it
  // and instead sends `page_size` + (for older pages) the keyset cursor below.
  page: z.string().regex(/^\d+$/).optional(),
  page_size: z.string().regex(/^\d+$/).optional(),
  // Keyset cursor for the newest-first scroll (`page` absent): return only
  // messages strictly older than (before_created_at, before_message_id) — the
  // oldest currently-loaded VISIBLE message. UTC ISO-8601 (Timestamps standard);
  // `datetime()` hardens it before the parameterized DO predicate.
  before_created_at: z.string().datetime().optional(),
  before_message_id: z.string().uuid().optional(),
  /**
   * When true AND the caller has support access (hub admin or hub team member),
   * internal messages (tool calls, tool results) are included in the response.
   * Silently ignored for non-support callers — it's a view preference, not an
   * authorization boundary.
   */
  include_internal: z
    .union([z.literal('true'), z.literal('false'), z.boolean()])
    .optional()
    .transform((v) => v === true || v === 'true'),
}).superRefine((q, ctx) => {
  // The keyset cursor is a compound (created_at, message_id) — both halves or
  // neither. A half-cursor would silently fall through to the newest page (the DO
  // applies the predicate only when both are present), so reject it rather than
  // paginate wrongly. Mirrors the exactly-one pattern used elsewhere in contracts.
  if ((q.before_created_at == null) !== (q.before_message_id == null)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'before_created_at and before_message_id must be provided together',
      path: [q.before_created_at == null ? 'before_created_at' : 'before_message_id'],
    });
  }
});

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

/** Team reference in a conversation */
export interface ConversationTeamRef {
  hub_team_id: string;
  team_name: string;
}

/** Agent reference in a conversation */
export interface ConversationAgentRef {
  agent_id: string;
  agent_name: string;
  /**
   * Role of the agent (`pilot` | `consultant` | …). Surfaced so the support
   * composer can partition the roster: pilot agents are Transfer targets, while
   * `consultant`-role agents are the @-mention consult targets (consultant-agents
   * H1.0). Optional for backward-compat with snapshots minted before this field —
   * a missing role reads as a non-consultant (i.e. a Transfer target).
   */
  agent_role?: string;
}

/** Kanban status configuration item */
export interface ConversationKanbanStatus {
  slug: string;
  name: string;
  color: string;
  isTerminalStatus?: boolean;
  allowed_next_statuses?: string[];
  /** Closing outcomes offered on the terminal transition (terminal status only). */
  outcomes?: KanbanOutcomeInput[];
  [key: string]: unknown;
}

/** Task hub available in metadata */
export interface AvailableTaskHub {
  hub_id: string;
  hub_name: string;
  app_channel_id: string | null;
  hub_environment?: 'preview' | 'production';
}

// Derived server-side from the viewer's user_id vs the conversation's claimer.
export type ClaimStatus = 'unclaimed' | 'claimed_by_this_user' | 'claimed_by_other_user';

/** The nav slice a conversation belongs to for a given viewer. */
export type SourceNav = 'chat' | 'task' | 'support';

/** Single conversation item returned by the get_conversations RPC */
export interface ConversationItemResponse {
  hub_id: string;
  user_id?: string;
  hub_team_user_id?: string;
  conversation_id: string;
  user_name?: string;
  hub_name?: string;
  hub_type?: 'task' | 'chat';
  /**
   * A task conversation's title, stamped from its first message when it was opened
   * (`deriveTaskTitle`, wayai#4640). `TASK_TITLE_VOICE` for a voice note, which clients render
   * as their localized label (`isVoiceTaskTitle`). Null or absent when it has none — the row
   * keeps its id-based label.
   */
  title?: string | null;
  hub_description?: string;
  hub_contexts?: string[];
  hub_environment?: 'preview' | 'production';
  kanban_statuses?: ConversationKanbanStatus[];
  project_name?: string;
  message_text?: string;
  message_status?: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | null;
  created_at?: string;
  sender_type?: string;
  receiver_type?: string;
  conversation_status?: string;
  /** Channel type of the last message — may be `app` for a team reply. */
  channel_type?: string;
  /** The conversation's bound customer channel (whatsapp/instagram/...) — distinct from `channel_type`. Feeds the support composer's 24h messaging-window gate. */
  current_channel_type?: string;
  /** Timestamp of the last customer (user) inbound message — feeds the 24h messaging-window gate. */
  last_inbound_at?: string;
  app_channel_id?: string;
  search_text?: string;
  sender_description?: string;
  receiver_description?: string;
  channel_description?: string;
  current_hub_team_id?: string;
  current_agent_id?: string;
  current_team_user_id?: string;
  current_responder_type?: string;
  /** Per-contact channel access: 'approved' | 'pending' | 'blocked'. Drives the support badge + composer gating. */
  access_status?: string;
  available_teams: ConversationTeamRef[];
  available_agents: ConversationAgentRef[];
  unread_message_count?: number;
  claim_status?: ClaimStatus;
  kanban_status?: string;
  ended_at?: string;
  // Set when conversation has been archived to R2. Null when ended_at is set
  // but archival hasn't completed (still in DO buffer). The frontend uses this
  // to decide between the archive endpoint and the active /messages endpoint.
  archived_at?: string | null;
  // Evaluator variables
  user_sentiment?: string;
  goal_achieved?: string;
  conversation_summary?: string;
  is_flagged?: boolean;
  flag_source?: FlagSource;
  /** The event instant captured on the last transition into a scheduling kanban
   *  status (ISO-8601 UTC). Absent on conversations not sitting in one. */
  scheduled_event_date?: string;
  /** True once the `event_due` schedule fired: the event has passed and a human
   *  still has to move the card. Independent of `conversation_status` — an
   *  `event_due` row surfaces in the support Team tab while the AI keeps
   *  responding. Cleared by any kanban transition and at close. */
  event_due?: boolean;
  // Nav slices the row belongs to for this viewer. A conversation can belong to
  // more than one (e.g. a viewer who is both the end user AND a team member of a
  // hub sees it in BOTH the owner nav and /support — the roles are orthogonal).
  // The backend emits the row into each matching slice; per-slice it carries the
  // single-element set for that slice, and the frontend unions them by id.
  source_navs?: SourceNav[];
}

/** Source of a conversation flag. Set by evaluator or monitor background agents. */
export type FlagSource = 'evaluator' | 'monitor' | 'both' | null;

/**
 * Merge an incoming flag source with an existing one.
 * Null/same-source → incoming; different existing source → 'both'.
 * Used by ConversationDO and HubDO to keep flag state consistent when the
 * same conversation is flagged by both evaluator and monitor.
 */
export function mergeFlagSource(
  existing: FlagSource,
  incoming: 'evaluator' | 'monitor',
): Exclude<FlagSource, null> {
  if (existing === null || existing === incoming) return incoming;
  return 'both';
}

/**
 * Hub metadata included in the conversations response.
 *
 * It carries the hub's localized END-USER presentation (`Partial<HubPresentationMaps>`,
 * `hub-presentation.ts`), which the client resolves for its UI locale with
 * `resolveHubPresentation`: `display_names` on every hub item; `welcome_messages` and
 * `starter_questions` only where the item is served for an end-user view — the snapshot's hubs the
 * viewer is an end user of, and the chat list's chat/task navs — which are the views that show
 * them. Each is absent when the hub sets none.
 */
export interface HubItemResponse extends Partial<HubPresentationMaps> {
  hub_id: string;
  hub_name?: string;
  hub_type?: 'chat' | 'task';
  hub_description?: string;
  hub_environment?: 'preview' | 'production';
  /** Server-owned disambiguator for sibling previews (see `wayai relabel`). Absent on production hubs. */
  preview_label?: string;
  /** Preview hub's pointer to its production clone — hub-family linkage for grouped hub pickers. Absent on production hubs and never-published previews. */
  production_hub_id?: string;
  /** Routing/responder policy. Drives UI affordances like "Transfer to Agent". */
  ai_mode?: AiMode;
  /** Hub's IANA timezone — the lens the UI renders message timestamps in. */
  timezone?: string;
  /** Hub language (en|pt|es) — localizes the pending-access notice + backend-sent text. */
  language?: string;
  /** Who may approve/block pending contacts: 'admin' (default) or 'team'. Drives the approve/block UI gating. */
  access_approval_role?: 'admin' | 'team';
  /** Per-hub override for the pending-access notice text (null ⇒ localized default). */
  access_request_message?: string;
  app_channel_id?: string;
  kanban_statuses?: ConversationKanbanStatus[];
  /** Presentational lane definitions for board grouping/filtering (see kanban.ts laneSchema). */
  lanes?: Array<{ slug: string; name: string; color?: string; order?: number }>;
  available_teams: Array<{ hub_team_id: string; team_name: string }>;
  // Carries `agent_role` (consultant-agents H1.0) so the client can partition the
  // roster (pilot = Transfer target, consultant = @-mention consult target). Consultant
  // entries appear ONLY on a team/admin viewer's payload — see buildComposerAgents.
  available_agents?: ConversationAgentRef[];
  /** Enabled team users (id = platform user_id) for resolving "Claimed by {name}". */
  available_team_users?: Array<{ id: string; name: string; team_id: string }>;
  /**
   * Whether the snapshot's user is an enabled hub admin on this hub. Drives
   * admin-only UI affordances (e.g. destructive deletes). Backend remains the
   * authoritative gate — this is a UI-visibility hint, not an auth decision.
   */
  is_current_user_admin?: boolean;
  // Pre-seeds Settings → States tab so it renders without a network round-trip.
  // Optional: omitted by older Worker versions; new clients fall back to a fetch.
  states?: StateResponse[];
  /**
   * The voice-calls CALL signal: whether this hub can take a voice call now. It drives
   * only the call button. True when the voice-calls gate is open, the hub is a chat hub,
   * and it holds an enabled `pilot_voice` agent on an enabled GPT-Live connection.
   * `POST /api/calls` re-checks all of it and can still refuse a call behind a
   * true signal: for the conversation (who holds it, the contact's access), the hub's pilot,
   * the connection's key, or the org's operations quota. Absent (an older Worker) reads as
   * false. A UI hint, not an authorization.
   */
  voice_calls_available?: boolean;
  /**
   * The voice-calls RECORDING signal: this hub's calls are recorded (the call signal is on,
   * and the voice agent a call runs on has `record_calls` on). It drives only the recording
   * warning the call button shows before a call. `POST /api/calls` records a call only when
   * its create says the caller acknowledged that warning, so a signal that is stale or absent
   * (an older Worker, read as false) never records a caller who was not warned: that call is
   * unrecorded. A UI hint, not an authorization.
   */
  voice_calls_recorded?: boolean;
  /**
   * The languages this hub's calls open with its own audio, each with the caption the call bar
   * shows while it plays and the version to fetch it by (`GET /api/calls/openings/:language`).
   * Set only with the call signal on; a language not listed, and a signal that is absent (an
   * older Worker) or stale, plays the platform's opening. A UI hint, not an authorization:
   * the opening route re-checks the caller's access, and answers 404 for a version that is no
   * longer the hub's, so the caller then hears the platform's opening.
   */
  voice_call_openings?: HubCallOpening[];
  /**
   * The voice-notes signal: this hub holds an enabled STT connection, so a voice note sent to
   * it is transcribed. It is the predicate the backend's transcription paths decide on
   * (`findEnabledConnectionByType(…, 'STT')`), over the same connection rows. It drives only
   * the composer's voice-message mic, for every sender (end user and team alike): anything
   * but `true` hides it, and an absent value (an older Worker) reads as false. A UI hint, not
   * an authorization: the backend still takes audio from a client that shows the mic anyway.
   */
  voice_notes_transcribed?: boolean;
}

/** RPC response from get_conversations (returned as JSON from the RPC) */
export interface GetConversationsRpcResponse {
  conversations: ConversationItemResponse[];
  hubs?: HubItemResponse[];
  metadata: {
    available_task_hubs?: AvailableTaskHub[];
    /** Pagination fields (present when status=ended) */
    total?: number;
    page?: number;
    page_size?: number;
    /**
     * ACTIVE list only: at least one hub in this response holds active rows the page does
     * not carry, so the client may offer "load more". The active list has no `total` — it
     * is a live set, and counting it exactly would cost a second full scan per hub on
     * every sidebar read — so these booleans are the whole signal. Absent means "no more"
     * (also what an older Worker returns).
     *
     * On the SUPPORT nav the two flags are separate because each answers for the ONE tab
     * that reads it. The sidebar renders one tab at a time, so a flag on any wider set puts
     * a button over a list it cannot change: press it, the window grows, the merged rows
     * land on the other tab, and the visible list is identical.
     */
    /**
     * SUPPORT: at least one hub holds AGENT-tab rows the page did not carry.
     * CHAT/TASK: whole-page recency truncation over the viewer's non-ended conversations.
     * Chat's sidebar is hub-driven — one row per chat hub — so there is nothing to page
     * there. The merged Chat of web and the mobile app (wayai#4640) reads it per hub:
     * that hub's In progress is exactly the page's set, so the flag drives the task list's
     * "N+" count and Load more and the Chat list's chip.
     */
    has_more?: boolean;
    /**
     * At least one hub holds rows needing a human (the support Team tab) that the page did
     * not carry. Support only — never set for a chat/task read.
     */
    needs_attention_has_more?: boolean;
  };
  /**
   * True when ClickHouse was cold and the response is a provisional empty
   * result — the client should keep polling until this flag disappears.
   */
  cold?: boolean;
}

/** File attachment in a message */
export interface MessageFileResponse {
  file_id?: string;
  bucket?: string;
  mime_type: string;
  file_name: string;
  file_short_name?: string;
  mime_category?: string;
  file_path: string;
  file_signed_url?: string;
  file_type?: string;
  file_size?: number;
}

/**
 * Diagnostic codes surfaced when AI message processing fails due to hub
 * misconfiguration. Each code has i18n copy (title + action) rendered in the
 * support view to guide the admin to a fix. `internal_error` is the
 * unknown-failure fallback and is the only code that logs at `error` level and
 * reaches Sentry.
 */
export type DiagnosticCode =
  | 'missing_pilot_connection'
  | 'hub_config_not_found'
  | 'credential_resolution_failed'
  | 'missing_api_key'
  | 'llm_provider_rejected'
  | 'rate_limit_exhausted'
  | 'internal_error'
  // A harness (external agent-loop) turn failed after retries and degraded to the
  // team fail-safe (harness-agents PR 2.10a).
  | 'harness_degraded'
  // An eval run's trigger turn resolved to no deliverable content — empty text and
  // no surviving attachment (e.g. an attachment-only trigger whose bytes are
  // missing). An author-setup problem, not a platform bug: fail the run, don't page.
  | 'eval_trigger_no_content'
  // A consult turn (consultant-agents PR 2) targeted an id that is not a
  // consultant-role agent on this hub. A config/authorization problem, not a
  // platform bug: finalize the turn cleanly, don't page.
  | 'consult_target_invalid'
  // The agent's model belongs to a different provider than its connection's
  // connector, so the completion would have posted that connection's api key to
  // a vendor the customer never configured. Refused before the request; the
  // model select accepts custom values, so this is operator-authored config.
  | 'model_connector_mismatch'
  // A native agent turn ran past the platform's turn deadline
  // (`platform_config.turn_deadline_seconds`): a provider call or a tool stopped answering.
  // The turn is ended and handed to the team, never retried.
  | 'turn_deadline_exceeded'
  // A native agent turn stopped before it finished because the instance running it was
  // reset (a deploy, an eviction). Recovery found it unfinished and handed the conversation
  // to the team; the turn is not run again.
  | 'turn_interrupted'
  // A resumed turn found that its interrupted attempt had called a tool the hub flags
  // irreversible, and that call's outcome was lost. It was not run again: the conversation was
  // handed to the team, with the call (`args.tool`, `args.arguments`) for a person to check.
  | 'irreversible_tool_interrupted';

export const DIAGNOSTIC_CODES: readonly DiagnosticCode[] = [
  'missing_pilot_connection',
  'hub_config_not_found',
  'credential_resolution_failed',
  'missing_api_key',
  'llm_provider_rejected',
  'rate_limit_exhausted',
  'internal_error',
  'harness_degraded',
  'eval_trigger_no_content',
  'consult_target_invalid',
  'model_connector_mismatch',
  'turn_deadline_exceeded',
  'turn_interrupted',
  'irreversible_tool_interrupted',
] as const;

/** Metadata shape for support-only error diagnostic messages (sender=system, receiver=team). */
export interface MessageErrorMetadata {
  is_error: true;
  error_code: DiagnosticCode;
  args?: Record<string, string>;
}

/**
 * `metadata.call` of a realtime call's transcript row, as the one utterance writer stores it
 * (`storeCallUtterances`): the call, who spoke, where on the provider session's timeline, and
 * `filler` on the voice agent's filler (said while the call waited on an answer, kept out of
 * every model history). The support inbox renders call rows from it (`getCallRowKind`), and the
 * call meter finds a call's rows by `call_id` and `speaker`.
 */
export interface CallMessageMetadata {
  call_id: string;
  speaker: Extract<CallParticipantType, 'user' | 'voice_agent'>;
  start_ms: number;
  end_ms: number;
  filler?: true;
}

/** Single message item returned by the get_messages RPC */
export interface MessageItemResponse {
  message_key: string;
  message_id: string;
  conversation_id: string;
  message_text: string;
  message_status?: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | null;
  sender_type: string;
  receiver_type: string;
  created_at: string;
  conversation_created_at?: string;
  hub_type?: string;
  channel_type?: string;
  channel_id?: string;
  is_last_message?: boolean;
  files: MessageFileResponse[];
  sender_description?: string;
  receiver_description?: string;
  channel_description?: string;
  conversation_start?: boolean;
  metadata?: Record<string, unknown> | null;
  /**
   * `false` on a row no model reads back (`message.keep_in_history = 0`); absent otherwise, as
   * on a live frame (`LiveUpdateMessage.keep_in_history`). Only a support read carries such a
   * row. The support view reads it to tell a realtime call's record apart: the pilot's answer
   * written for the voice agent, never said to the caller as written, from the same row once it
   * was delivered as a message; and the voice agent's filler.
   */
  keep_in_history?: false;
  /**
   * Non-null on a row that belongs to an internal consult exchange (a team member's
   * private question to a `consultant`-role agent + its replies — consultant-agents
   * H1.0). The support chat view keys its collapsible consult-thread grouping on it;
   * absent/undefined on every customer-facing and copilot row. Support-only: the
   * whole consult frame is contained to support subscribers by the recipient policy,
   * so this never reaches the end user's client.
   */
  consult_thread_id?: string | null;
}

// ---------------------------------------------------------------------------
// ConversationDO composite read: ChatView
// ---------------------------------------------------------------------------

/**
 * Raw file row as stored in ConversationDO's `conversation_file` table,
 * returned attached to the owning message inside `ChatView.messages`.
 * Shape mirrors the SQLite row so the route layer can map it to
 * `MessageFileResponse` without additional DO round-trips.
 */
export interface ChatViewFile {
  file_id: string;
  message_id: string;
  /** Only populated by `getFiles` (archival read); omitted when the row is
   *  joined onto a message in `getMessages` / `getChatView` to keep payloads small. */
  conversation_id?: string | null;
  file_name: string | null;
  /** R2 object key — storage layout, never shown to an agent or an end user. */
  file_path: string | null;
  bucket: string | null;
  mime_type: string | null;
  file_size: number | null;
  created_at: string | null;
  /**
   * Model-facing address, `conversation/<file-name>` — what an agent passes to
   * `read_file` / `send_files`. Derived on read from the conversation's own file
   * list (see `conversation-file-alias.ts`), never stored, so a rename or a
   * deletion re-derives instead of leaving a stale address behind. Null only for
   * a row with no `file_id`, which nothing can address.
   */
  path?: string | null;
}

/**
 * Raw message row plus any files joined by `message_id`.
 * `files` is always populated — an empty array when the message has no
 * attachments. This removes the `files=[]` fallback at route level because
 * the DO now joins the two tables in a single SQL transaction.
 */
export interface ChatViewMessage {
  message_id: string;
  conversation_id: string | null;
  message_text: string | null;
  message_type: string | null;
  sender_type: string | null;
  sender_id: string | null;
  sender_name: string | null;
  receiver_type: string | null;
  receiver_id: string | null;
  channel_type: string | null;
  channel_message_sid: string | null;
  keep_in_history: number | boolean | null;
  agent_id: string | null;
  agent_role: string | null;
  message_status: string | null;
  metadata: string | Record<string, unknown> | null;
  /** Consult-thread tag (consultant-agents H1.0) — selected by `CHAT_MESSAGE_COLUMNS`
   *  and surfaced so the chat route can pass it through to `MessageItemResponse`. */
  consult_thread_id: string | null;
  created_at: string;
  files: ChatViewFile[];
}

/**
 * Minimal conversation metadata returned by `getChatView`.
 * Only the fields used by the chat route to render the header / gate hub
 * ownership are returned — keeps the RPC payload small while still
 * eliminating a second DO round-trip per chat load.
 */
export interface ChatViewConversation {
  conversation_id: string;
  hub_id: string | null;
  user_id: string | null;
  status: string | null;
  current_responder_type: string | null;
  current_agent_id: string | null;
  current_channel_id: string | null;
  current_channel_type: string | null;
  kanban_status: string | null;
  created_at: string | null;
  updated_at: string | null;
  hub_type: string | null;
  // current_team_user_id is a platform user_id, not a hub_team_user_id.
  current_hub_team_id: string | null;
  current_team_user_id: string | null;
}

/**
 * Composite read response returned by `ConversationDO.getChatView`.
 * Combines messages (with joined files) and conversation metadata in a
 * single DO round-trip, replacing the legacy 2-fetch pattern
 * (v2GetMessages + v2GetConversation).
 */
export interface ChatView {
  conversation: ChatViewConversation | null;
  messages: ChatViewMessage[];
  total: number;
  /**
   * agent_id → agent_name, sourced from ConversationDO's LOCAL hub-config cache
   * (no HubDO round-trip) so the chat route can label agent bubbles without the
   * heavyweight `listAgents` roster read. Optional + `'Agent'` route fallback:
   * an empty/absent map (cold DO cache) degrades exactly like the prior
   * `listAgents().catch(() => [])`. May be slightly stale on an agent rename —
   * acceptable because the name only surfaces as an icon tooltip.
   */
  agent_names?: Record<string, string>;
}
